import React, { useEffect, useMemo, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Download, FileSpreadsheet, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';

import { dispatchAPI, contractsAPI } from '@/lib/api';

// Helper to download a blob as a file
function downloadBlob(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

export default function DispatchPlanReportPage() {
  const [customers, setCustomers] = useState([]);
  const [tab, setTab] = useState('plan');

  useEffect(() => {
    (async () => {
      try {
        const res = await contractsAPI.activeCustomers();
        setCustomers(res.data?.customers || res.data || []);
      } catch {
        toast.error('Failed to load customers');
      }
    })();
  }, []);

  return (
    <div className="space-y-4 p-4 md:p-6">
      <div>
        <h1 className="font-heading text-xl font-bold tracking-tight flex items-center gap-2">
          <FileSpreadsheet className="w-5 h-5 text-blue-600" /> Customer Dispatch Reports
        </h1>
        <p className="text-xs text-muted-foreground mt-1">
          Export dispatch plan matrices and raw-material stock for selected items, by customer.
        </p>
      </div>

      <Tabs value={tab} onValueChange={setTab} data-testid="dispatch-report-tabs">
        <TabsList>
          <TabsTrigger value="plan" data-testid="tab-plan">Dispatch Plan</TabsTrigger>
          <TabsTrigger value="rmstock" data-testid="tab-rmstock">RM / Job Work Stock</TabsTrigger>
          <TabsTrigger value="history" data-testid="tab-history">Dispatch History</TabsTrigger>
          <TabsTrigger value="details" data-testid="tab-details">Dispatch Details</TabsTrigger>
        </TabsList>

        <TabsContent value="plan">
          <DispatchPlan customers={customers} />
        </TabsContent>
        <TabsContent value="rmstock">
          <RmJwStock customers={customers} />
        </TabsContent>
        <TabsContent value="history">
          <DispatchHistory customers={customers} />
        </TabsContent>

        <TabsContent value="details">
          <DispatchDetails customers={customers} />
        </TabsContent>
      </Tabs>
    </div>
  );
}

// ====================================================================
// Sub-component: Dispatch Plan matrix
// ====================================================================

function DispatchPlan({ customers }) {
  const today = new Date();
  const monthStart = new Date(today.getFullYear(), today.getMonth(), 1);
  const fmt = (d) => d.toISOString().slice(0, 10);

  const [customerId, setCustomerId] = useState('');
  const [dateFrom, setDateFrom] = useState(fmt(monthStart));
  const [dateTo, setDateTo] = useState(fmt(today));
  const [items, setItems] = useState([]);
  const [selected, setSelected] = useState([]);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);

  const loadItems = async () => {
    if (!customerId) {
      toast.error('Select a customer first');
      return;
    }
    setLoading(true);
    setSelected([]);
    try {
      const res = await dispatchAPI.getCustomerDispatchedItems({
        customer_id: customerId,
        date_from: dateFrom,
        date_to: dateTo,
      });
      setItems(res.data?.items || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load dispatched items');
    } finally {
      setLoading(false);
    }
  };

  const toggleAll = () => {
    if (selected.length === items.length) setSelected([]);
    else setSelected(items.map(i => i.item_id));
  };

  const toggle = (id) => {
    setSelected(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  const exportXlsx = async () => {
    if (selected.length === 0) { toast.error('Select at least one item'); return; }
    setExporting(true);
    try {
      const res = await dispatchAPI.exportDispatchPlan({
        customer_id: customerId,
        date_from: dateFrom,
        date_to: dateTo,
        item_ids: selected,
      });
      const cn = (customers.find(c => c.id === customerId)?.customer_name || 'Customer').replace(/\W+/g, '_');
      downloadBlob(res.data, `Dispatch_Plan_${cn}_${dateFrom}_to_${dateTo}.xlsx`);
      toast.success('Report downloaded');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to generate report');
    } finally {
      setExporting(false);
    }
  };

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base">Dispatch Plan (matrix)</CardTitle>
        <p className="text-xs text-muted-foreground">
          Cross-tab of date × selected parts, showing the actual dispatched quantity per day for the selected period. Days with no dispatch are shown as <strong>0</strong>.
        </p>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <div>
            <Label className="text-xs">Customer *</Label>
            <Select value={customerId} onValueChange={setCustomerId}>
              <SelectTrigger className="h-9 text-xs mt-1" data-testid="plan-customer-select">
                <SelectValue placeholder="Select customer" />
              </SelectTrigger>
              <SelectContent className="max-h-72">
                {customers.map(c => (
                  <SelectItem key={c.id} value={c.id}>{c.customer_name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Date From *</Label>
            <Input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} className="h-9 text-xs mt-1" data-testid="plan-date-from" />
          </div>
          <div>
            <Label className="text-xs">Date To *</Label>
            <Input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} className="h-9 text-xs mt-1" data-testid="plan-date-to" />
          </div>
          <div className="flex items-end gap-2">
            <Button onClick={loadItems} disabled={loading || !customerId} className="h-9" data-testid="plan-load-items">
              <RefreshCw className={`w-3.5 h-3.5 mr-1 ${loading ? 'animate-spin' : ''}`} />
              Load Items
            </Button>
          </div>
        </div>

        {items.length > 0 && (
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <p className="text-xs text-muted-foreground">{items.length} items dispatched in this period • {selected.length} selected</p>
              <Button size="sm" variant="outline" onClick={toggleAll} className="h-7 text-xs" data-testid="plan-select-all">
                {selected.length === items.length ? 'Clear all' : 'Select all'}
              </Button>
            </div>
            <div className="border rounded-md max-h-[320px] overflow-y-auto">
              <table className="w-full text-xs">
                <thead className="bg-muted/50 sticky top-0">
                  <tr>
                    <th className="p-2 w-8"></th>
                    <th className="p-2 text-left">Item Name</th>
                    <th className="p-2 text-left">Code</th>
                    <th className="p-2 text-center">UOM</th>
                    <th className="p-2 text-right">Total Qty</th>
                    <th className="p-2 text-center">DCs</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {items.map(it => (
                    <tr key={it.item_id} onClick={() => toggle(it.item_id)} className="hover:bg-muted/30 cursor-pointer" data-testid={`plan-item-${it.item_unique_id}`}>
                      <td className="p-2"><Checkbox checked={selected.includes(it.item_id)} onCheckedChange={() => toggle(it.item_id)} /></td>
                      <td className="p-2">{it.item_name}</td>
                      <td className="p-2 font-mono">{it.item_unique_id}</td>
                      <td className="p-2 text-center text-muted-foreground">{it.uom}</td>
                      <td className="p-2 text-right font-mono">{it.total_qty}</td>
                      <td className="p-2 text-center text-muted-foreground">{it.dc_count}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <Button onClick={exportXlsx} disabled={exporting || selected.length === 0} data-testid="plan-export-btn">
              <Download className="w-3.5 h-3.5 mr-1" />
              {exporting ? 'Generating…' : 'Generate & Download XLSX'}
            </Button>
          </div>
        )}
        {items.length === 0 && customerId && !loading && (
          <p className="text-xs text-muted-foreground italic">No items dispatched to this customer in the selected period — try a wider date range.</p>
        )}
      </CardContent>
    </Card>
  );
}

// ====================================================================
// Sub-component: Raw Material / Job Work stock (direct selection)
// ====================================================================

function RmJwStock({ customers }) {
  const [customerId, setCustomerId] = useState('');
  const [items, setItems] = useState([]);
  const [selected, setSelected] = useState([]);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [linkedToPrograms, setLinkedToPrograms] = useState(false);

  const loadItems = async () => {
    if (!customerId) { toast.error('Select a customer first'); return; }
    setLoading(true);
    setSelected([]);
    try {
      const res = await dispatchAPI.getCustomerRmJwItems({ customer_id: customerId });
      setItems(res.data?.items || []);
      setLinkedToPrograms(!!res.data?.linked_to_programs);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load items');
    } finally {
      setLoading(false);
    }
  };

  const toggle = (id) => setSelected(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  const toggleAll = () => setSelected(selected.length === items.length ? [] : items.map(i => i.item_id));

  const exportXlsx = async () => {
    if (selected.length === 0) { toast.error('Select at least one item'); return; }
    setExporting(true);
    try {
      const res = await dispatchAPI.exportRmJwStock({
        customer_id: customerId,
        item_ids: selected,
      });
      const cn = (customers.find(c => c.id === customerId)?.customer_name || 'Customer').replace(/\W+/g, '_');
      const today = new Date().toISOString().slice(0, 10);
      downloadBlob(res.data, `RM_JW_Stock_${cn}_${today}.xlsx`);
      toast.success('Report downloaded');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to generate report');
    } finally {
      setExporting(false);
    }
  };

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base">Raw Material / Job Work Item Stock</CardTitle>
        <p className="text-xs text-muted-foreground">
          Live stock for selected <strong>Raw Material</strong> (received from customer) and <strong>Job Work Item</strong> entries. The export shows <strong>OK Stock</strong> (available — same formula as Item Master, excluding QC/Quarantine/Inbound-Staging/Rejection), plus separate <strong>Quarantined</strong> and <strong>Pending Inspection</strong> (Inbound Staging + QC) columns.
        </p>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div>
            <Label className="text-xs">Customer *</Label>
            <Select value={customerId} onValueChange={setCustomerId}>
              <SelectTrigger className="h-9 text-xs mt-1" data-testid="rm-customer-select">
                <SelectValue placeholder="Select customer" />
              </SelectTrigger>
              <SelectContent className="max-h-72">
                {customers.map(c => (
                  <SelectItem key={c.id} value={c.id}>{c.customer_name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="md:col-span-2 flex items-end gap-2">
            <Button onClick={loadItems} disabled={loading || !customerId} className="h-9" data-testid="rm-load-items">
              <RefreshCw className={`w-3.5 h-3.5 mr-1 ${loading ? 'animate-spin' : ''}`} />
              Load Items
            </Button>
          </div>
        </div>

        {items.length > 0 && (
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <p className="text-xs text-muted-foreground">
                {items.length} item(s) {linkedToPrograms ? 'linked to this customer\'s programs' : '(no program link — showing all RM + Job Work items)'} • {selected.length} selected
              </p>
              <Button size="sm" variant="outline" onClick={toggleAll} className="h-7 text-xs" data-testid="rm-select-all">
                {selected.length === items.length ? 'Clear all' : 'Select all'}
              </Button>
            </div>
            <div className="border rounded-md max-h-[360px] overflow-y-auto">
              <table className="w-full text-xs">
                <thead className="bg-muted/50 sticky top-0">
                  <tr>
                    <th className="p-2 w-8"></th>
                    <th className="p-2 text-left">Item Name</th>
                    <th className="p-2 text-left">Code</th>
                    <th className="p-2 text-left">Type</th>
                    <th className="p-2 text-center">UOM</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {items.map(it => (
                    <tr key={it.item_id} onClick={() => toggle(it.item_id)} className="hover:bg-muted/30 cursor-pointer" data-testid={`rm-item-${it.item_unique_id}`}>
                      <td className="p-2"><Checkbox checked={selected.includes(it.item_id)} onCheckedChange={() => toggle(it.item_id)} /></td>
                      <td className="p-2">{it.item_name}</td>
                      <td className="p-2 font-mono">{it.item_unique_id}</td>
                      <td className="p-2">
                        <span className={`text-[10px] px-1.5 py-0.5 rounded border ${it.item_type === 'Raw Material' ? 'bg-blue-50 text-blue-700 border-blue-300' : 'bg-purple-50 text-purple-700 border-purple-300'}`}>
                          {it.item_type}
                        </span>
                      </td>
                      <td className="p-2 text-center text-muted-foreground">{it.uom}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <Button onClick={exportXlsx} disabled={exporting || selected.length === 0} data-testid="rm-export-btn">
              <Download className="w-3.5 h-3.5 mr-1" />
              {exporting ? 'Generating…' : 'Generate & Download XLSX'}
            </Button>
          </div>
        )}
        {items.length === 0 && customerId && !loading && (
          <p className="text-xs text-muted-foreground italic">No Raw Material / Job Work items found. Confirm such items exist in Item Master.</p>
        )}
      </CardContent>
    </Card>
  );
}

// ====================================================================
// Sub-component: Dispatch History (flat per-line export, filter by customer + parts + range)
// ====================================================================

function DispatchHistory({ customers }) {
  const today = new Date();
  const monthStart = new Date(today.getFullYear(), today.getMonth(), 1);
  const fmt = (d) => d.toISOString().slice(0, 10);

  const [customerId, setCustomerId] = useState('');
  const [dateFrom, setDateFrom] = useState(fmt(monthStart));
  const [dateTo, setDateTo] = useState(fmt(today));
  const [dateBasis, setDateBasis] = useState('dc'); // 'dc' | 'invoice'
  const [onlyReconciled, setOnlyReconciled] = useState(false);
  const [items, setItems] = useState([]);
  const [selected, setSelected] = useState([]);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);

  const loadItems = async () => {
    if (!customerId) { toast.error('Select a customer first'); return; }
    setLoading(true);
    setSelected([]);
    try {
      const res = await dispatchAPI.getCustomerDispatchedItems({
        customer_id: customerId, date_from: dateFrom, date_to: dateTo,
      });
      setItems(res.data?.items || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load dispatched items');
    } finally {
      setLoading(false);
    }
  };

  const toggleAll = () => {
    if (selected.length === items.length) setSelected([]);
    else setSelected(items.map(i => i.item_id));
  };
  const toggle = (id) => {
    setSelected(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  const exportXlsx = async () => {
    setExporting(true);
    try {
      const res = await dispatchAPI.exportDispatchHistory({
        customer_id: customerId,
        date_from: dateFrom,
        date_to: dateTo,
        // empty array = ALL parts dispatched to this customer in range
        item_ids: selected,
        date_basis: dateBasis,
        only_reconciled: onlyReconciled,
      });
      const cn = (customers.find(c => c.id === customerId)?.customer_name || 'Customer').replace(/\W+/g, '_');
      downloadBlob(res.data, `Dispatch_History_${cn}_${dateFrom}_to_${dateTo}.xlsx`);
      toast.success('Report downloaded');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to generate report');
    } finally {
      setExporting(false);
    }
  };

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base">Dispatch History (flat list)</CardTitle>
        <p className="text-xs text-muted-foreground">
          Per-DC-line export with Dispatch Date, Part Number, and the Invoice # (when reconciled) or DC # (otherwise). Leave Part Number empty to include all parts dispatched to this customer in the date range.
        </p>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
          <div>
            <Label className="text-xs">Customer *</Label>
            <Select value={customerId} onValueChange={setCustomerId}>
              <SelectTrigger className="h-9 text-xs mt-1" data-testid="history-customer-select">
                <SelectValue placeholder="Select customer" />
              </SelectTrigger>
              <SelectContent className="max-h-72">
                {customers.map(c => (<SelectItem key={c.id} value={c.id}>{c.customer_name}</SelectItem>))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Date Basis</Label>
            <Select value={dateBasis} onValueChange={setDateBasis}>
              <SelectTrigger className="h-9 text-xs mt-1" data-testid="history-date-basis">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="dc">DC Date</SelectItem>
                <SelectItem value="invoice">Invoice Date (reconciled only)</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Date From *</Label>
            <Input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} className="h-9 text-xs mt-1" data-testid="history-date-from" />
          </div>
          <div>
            <Label className="text-xs">Date To *</Label>
            <Input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} className="h-9 text-xs mt-1" data-testid="history-date-to" />
          </div>
          <div className="flex items-end gap-2">
            <Button onClick={loadItems} disabled={loading || !customerId} className="h-9" data-testid="history-load-items">
              <RefreshCw className={`w-3.5 h-3.5 mr-1 ${loading ? 'animate-spin' : ''}`} />
              Load Items
            </Button>
          </div>
        </div>

        <div className="flex items-center gap-2 pl-1">
          <Checkbox id="history-only-rec" checked={onlyReconciled} onCheckedChange={v => setOnlyReconciled(!!v)} data-testid="history-only-reconciled" />
          <Label htmlFor="history-only-rec" className="text-xs cursor-pointer">
            Only reconciled dispatches (DCs with a customer invoice number)
          </Label>
        </div>

        {items.length > 0 && (
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <p className="text-xs text-muted-foreground">
                {items.length} parts dispatched in this period • {selected.length} selected (empty = all)
              </p>
              <Button size="sm" variant="outline" onClick={toggleAll} className="h-7 text-xs" data-testid="history-select-all">
                {selected.length === items.length ? 'Clear all' : 'Select all'}
              </Button>
            </div>
            <div className="border rounded-md max-h-[320px] overflow-y-auto">
              <table className="w-full text-xs">
                <thead className="bg-muted/50 sticky top-0">
                  <tr>
                    <th className="p-2 w-8"></th>
                    <th className="p-2 text-left">Item Name</th>
                    <th className="p-2 text-left">Code</th>
                    <th className="p-2 text-center">UOM</th>
                    <th className="p-2 text-right">Total Qty</th>
                    <th className="p-2 text-center">DCs</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {items.map(it => (
                    <tr key={it.item_id} onClick={() => toggle(it.item_id)} className="hover:bg-muted/30 cursor-pointer" data-testid={`history-item-${it.item_unique_id}`}>
                      <td className="p-2"><Checkbox checked={selected.includes(it.item_id)} onCheckedChange={() => toggle(it.item_id)} /></td>
                      <td className="p-2">{it.item_name}</td>
                      <td className="p-2 font-mono">{it.item_unique_id}</td>
                      <td className="p-2 text-center">{it.uom}</td>
                      <td className="p-2 text-right">{it.total_qty}</td>
                      <td className="p-2 text-center">{it.dc_count}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        <div className="flex items-center justify-end gap-2 pt-1">
          <Button onClick={exportXlsx} disabled={exporting || !customerId} className="h-9" data-testid="history-export-btn">
            <Download className={`w-3.5 h-3.5 mr-1 ${exporting ? 'animate-spin' : ''}`} />
            Download Excel
          </Button>
        </div>
        {!customerId && (
          <p className="text-xs text-muted-foreground italic">Select a customer and date range, then click Download Excel. Item selection is optional.</p>
        )}
      </CardContent>
    </Card>
  );
}

// ============================================================
// Dispatch Details — customer format: Part No / Qty / W.O (trace id) /
// customer DC no / PO no / BHPL invoice no
// ============================================================
function DispatchDetails({ customers }) {
  const today = new Date();
  const monthStart = new Date(today.getFullYear(), today.getMonth(), 1);
  const fmt = (d) => d.toISOString().slice(0, 10);

  const [customerId, setCustomerId] = useState('');
  const [dateFrom, setDateFrom] = useState(fmt(monthStart));
  const [dateTo, setDateTo] = useState(fmt(today));
  const [items, setItems] = useState([]);
  const [selected, setSelected] = useState([]);
  const [loadingItems, setLoadingItems] = useState(false);
  const [report, setReport] = useState(null);
  const [running, setRunning] = useState(false);
  const [exporting, setExporting] = useState(false);

  const customerName = customers.find(c => c.id === customerId)?.customer_name || '';
  const shortCode = customerName.split(/\s+/).slice(0, 4).map(w => w[0]).join('').toUpperCase() || 'CUST';

  const loadItems = async () => {
    if (!customerId) { toast.error('Select a customer first'); return; }
    setLoadingItems(true); setSelected([]); setReport(null);
    try {
      const res = await dispatchAPI.getCustomerDispatchedItems({ customer_id: customerId, date_from: dateFrom, date_to: dateTo });
      setItems(res.data?.items || []);
      if (!(res.data?.items || []).length) toast.info('No parts dispatched to this customer in the selected range');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load dispatched parts');
    } finally { setLoadingItems(false); }
  };

  const runReport = async () => {
    if (!customerId) { toast.error('Select a customer first'); return; }
    setRunning(true);
    try {
      const res = await dispatchAPI.dispatchDetails({ customer_id: customerId, date_from: dateFrom, date_to: dateTo, item_ids: selected });
      setReport(res.data);
      if (!res.data.rows.length) toast.info('No dispatch lines match these filters');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to generate report');
    } finally { setRunning(false); }
  };

  const exportXlsx = async () => {
    setExporting(true);
    try {
      const res = await dispatchAPI.exportDispatchDetails({ customer_id: customerId, date_from: dateFrom, date_to: dateTo, item_ids: selected });
      downloadBlob(res.data, `Dispatch_Details_${customerName.replace(/\W+/g, '_')}_${dateFrom}_to_${dateTo}.xlsx`);
      toast.success('Report downloaded');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to export');
    } finally { setExporting(false); }
  };

  const toggle = (id) => setSelected(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base">Dispatch Details</CardTitle>
        <p className="text-xs text-muted-foreground">
          Customer format — Part No, Qty, W/O (traceability ID of the dispatched lot), customer DC No, PO No and BHPL invoice No.
          Cancelled DCs are excluded. Pick specific parts, or leave all unticked for every part dispatched in the range.
        </p>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <div>
            <Label className="text-xs">Customer *</Label>
            <Select value={customerId} onValueChange={(v) => { setCustomerId(v); setItems([]); setSelected([]); setReport(null); }}>
              <SelectTrigger className="h-9 text-xs mt-1" data-testid="details-customer-select"><SelectValue placeholder="Select customer" /></SelectTrigger>
              <SelectContent className="max-h-72">
                {customers.map(c => (<SelectItem key={c.id} value={c.id}>{c.customer_name}</SelectItem>))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Date From *</Label>
            <Input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} className="h-9 text-xs mt-1" data-testid="details-date-from" />
          </div>
          <div>
            <Label className="text-xs">Date To *</Label>
            <Input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} className="h-9 text-xs mt-1" data-testid="details-date-to" />
          </div>
          <div className="flex items-end gap-2">
            <Button variant="outline" onClick={loadItems} disabled={loadingItems || !customerId} className="h-9" data-testid="details-load-items">
              <RefreshCw className={`w-3.5 h-3.5 mr-1 ${loadingItems ? 'animate-spin' : ''}`} /> Load Parts
            </Button>
          </div>
        </div>

        {items.length > 0 && (
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <p className="text-xs text-muted-foreground">
                Parts dispatched in range — {selected.length ? `${selected.length} selected` : 'all parts included'}
              </p>
              <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => setSelected(selected.length === items.length ? [] : items.map(i => i.item_id))} data-testid="details-toggle-all">
                {selected.length === items.length ? 'Clear selection' : 'Select all'}
              </Button>
            </div>
            <div className="border rounded-md max-h-44 overflow-auto divide-y">
              {items.map(it => (
                <label key={it.item_id} className="flex items-center gap-2 px-2.5 py-1.5 text-xs cursor-pointer hover:bg-slate-50">
                  <Checkbox checked={selected.includes(it.item_id)} onCheckedChange={() => toggle(it.item_id)} data-testid={`details-item-${it.item_id}`} />
                  <span className="font-mono text-[11px]">{it.item_unique_id}</span>
                  <span className="text-slate-500 truncate">{it.item_name}</span>
                  <span className="ml-auto text-slate-400">{it.total_qty} {it.uom}</span>
                </label>
              ))}
            </div>
          </div>
        )}

        <div className="flex flex-wrap items-center gap-2">
          <Button onClick={runReport} disabled={running || !customerId} className="h-9" data-testid="details-run">
            <RefreshCw className={`w-3.5 h-3.5 mr-1 ${running ? 'animate-spin' : ''}`} /> Generate
          </Button>
          <Button variant="outline" onClick={exportXlsx} disabled={exporting || !customerId} className="h-9" data-testid="details-export">
            <Download className="w-3.5 h-3.5 mr-1" /> {exporting ? 'Exporting…' : 'Download Excel'}
          </Button>
        </div>

        {report && (
          <div className="space-y-2" data-testid="details-result">
            <p className="text-xs text-muted-foreground">
              {report.total_rows} line(s) · total qty <b>{report.total_qty}</b> · {report.customer_name} · {report.date_from} to {report.date_to}
            </p>
            <div className="border rounded-md overflow-auto max-h-[460px]">
              <table className="w-full text-xs">
                <thead className="bg-slate-900 text-white sticky top-0">
                  <tr>
                    <th className="px-2 py-1.5 text-left font-medium">Dispatch Date</th>
                    <th className="px-2 py-1.5 text-left font-medium">Part No</th>
                    <th className="px-2 py-1.5 text-right font-medium">Qty</th>
                    <th className="px-2 py-1.5 text-left font-medium">{shortCode} W/O</th>
                    <th className="px-2 py-1.5 text-left font-medium">{shortCode} DC NO</th>
                    <th className="px-2 py-1.5 text-left font-medium">PO NO</th>
                    <th className="px-2 py-1.5 text-left font-medium">BHPL Invoice NO</th>
                  </tr>
                </thead>
                <tbody>
                  {report.rows.length === 0 ? (
                    <tr><td colSpan={7} className="text-center text-slate-400 py-6">No dispatch lines match the selected filters</td></tr>
                  ) : report.rows.map((r, i) => (
                    <tr key={i} className="border-b last:border-0 hover:bg-slate-50" data-testid={`details-row-${i}`}>
                      <td className="px-2 py-1 font-mono text-[11px] text-slate-500">{r.dispatch_date}</td>
                      <td className="px-2 py-1 font-mono text-[11px]">{r.part_no}</td>
                      <td className="px-2 py-1 text-right">{r.quantity}</td>
                      <td className="px-2 py-1 font-mono text-[11px]">{r.work_order_no || <span className="text-slate-300">—</span>}</td>
                      <td className="px-2 py-1 font-mono text-[11px]">{r.customer_dc_no || <span className="text-slate-300">—</span>}</td>
                      <td className="px-2 py-1 font-mono text-[11px]">{r.po_no || <span className="text-slate-300">—</span>}</td>
                      <td className="px-2 py-1 font-mono text-[11px]">{r.invoice_no || <span className="text-slate-300">—</span>}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
